// File Location: src/App.jsx

import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import PrivateRoute from "./component/common/PrivateRoute";
import usePushNotifications from "./hooks/usePushNotifications";

// Pages
import HomePage from "./screens/HomePage";
import LoginPage from "./screens/auth/LoginPage";
import RoleSelect from "./screens/auth/RoleSelect";
import AdminDashboard from "./screens/dashboard/AdminDashboard";
import SystemAdminDashboard from "./screens/dashboard/SystemAdminDashboard";
import HospitalDashboard from "./screens/dashboard/HospitalDashboard";
import DoctorDashboard from "./screens/dashboard/DoctorDashboard";
import PolicyDashboard from "./screens/dashboard/PolicyDashboard";
import FinanceDashboard from "./screens/dashboard/FinanceDashboard";
import UnauthorizedPage from "./screens/auth/Unauthorized";

// Import Tailwind CSS
import "./assets/styles/tailwind.css";
import HospitalRegistration from "./screens/hospital/HospitalRegistration";
import HospitalApplicationDetails from "./screens/admin/HospitalApplicationDetails";
import HospitalEdit from "./screens/admin/HospitalEdit";
import ApplicationDetails from "./screens/hospital/ApplicationDetails";
import NewPatientApplicationHospital from "./screens/hospital/NewPatientApplication";
import DoctorApplicationReview from "./screens/doctor/DoctorReview";
import LeadGeneratorRegistration from "./screens/lead/LeadRegistration";
import LeadGeneratorApplicationDetails from "./screens/lead/LeadGeneratorApplicationDetails";
import LeadDashboard from "./screens/dashboard/LeadDashboard";
import PolicyVerificationForm from "./screens/policy/PolicyVerificationForm";
import PolicyApplicationDetails from "./screens/policy/PolicyApplicationDetails";
import LeadApplicationDetails from "./screens/lead/ApplicationDetails";
import NewPatientApplicationLead from "./screens/lead/NewPatientApplication";
import TestKYPPDF from "./screens/TestKYPPDF";
import FinancialApplicationsList from "./screens/financial/FinancialApplicationsList";
import FinancialApplicationReview from "./screens/financial/FinancialReview";
import DocumentsPage from "./screens/common/DocumentsPage";

// App Router Component
function AppRouter() {
  // Initialize push notifications
  usePushNotifications();

  return (
    <Router>
      <div className="App">
        <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/select-role" element={<RoleSelect />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route
              path="/hospital-registration"
              element={<HospitalRegistration />}
            />
            <Route
              path="/lead-registration"
              element={<LeadGeneratorRegistration />}
            />
            {/* Protected Routes */}
            <Route
              path="/admin/dashboard"
              element={
                <PrivateRoute requiredRole="SuperAdmin">
                  <AdminDashboard />
                </PrivateRoute>
              }
            />

            <Route
              path="/system-admin/dashboard"
              element={
                <PrivateRoute requiredRole="SystemAdmin">
                  <SystemAdminDashboard />
                </PrivateRoute>
              }
            />

            <Route
              path="/lead-generator/dashboard"
              element={
                <PrivateRoute requiredRole="LeadGenerator">
                  <LeadDashboard />
                </PrivateRoute>
              }
            />

            <Route
              path="/hospital/dashboard"
              element={
                <PrivateRoute requiredRole="Hospital">
                  <HospitalDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/hospital/application/:applicationId"
              element={
                <PrivateRoute requiredRole="Hospital">
                  <ApplicationDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="/hospital/new-application"
              element={
                <PrivateRoute requiredRole="Hospital">
                  <NewPatientApplicationHospital />
                </PrivateRoute>
              }
            />
            <Route
              path="/doctor/application-review/:applicationId"
              element={
                <PrivateRoute requiredRole="Doctor">
                  <DoctorApplicationReview />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/hospital-details/:applicationId"
              element={
                <PrivateRoute requiredRole="SuperAdmin">
                  <HospitalApplicationDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/hospital-edit/:applicationId"
              element={
                <PrivateRoute requiredRole="SuperAdmin">
                  <HospitalEdit />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/lead-details/:applicationId"
              element={
                <PrivateRoute requiredRole="SuperAdmin">
                  <LeadGeneratorApplicationDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="/doctor/dashboard"
              element={
                <PrivateRoute requiredRole="Doctor">
                  <DoctorDashboard />
                </PrivateRoute>
              }
            />

            <Route
              path="/policy/dashboard"
              element={
                <PrivateRoute requiredRole="PolicyWriter">
                  <PolicyDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/policy/details/:applicationId"
              element={
                <PrivateRoute requiredRole="PolicyWriter">
                  <PolicyApplicationDetails />
                </PrivateRoute>
              }
            />

            <Route
              path="/lead/createapplication"
              element={
                <PrivateRoute requiredRole="LeadGenerator">
                  <NewPatientApplicationLead />
                </PrivateRoute>
              }
            />

            {/* Test PDF Generator Route */}
            <Route
              path="/test-kyp-pdf"
              element={<TestKYPPDF />}
            />

            <Route
              path="/policy/form"
              element={
                <PrivateRoute requiredRole="PolicyWriter">
                  <PolicyVerificationForm />
                </PrivateRoute>
              }
            />

            <Route
              path="/finance/dashboard"
              element={
                <PrivateRoute requiredRole="FinancialTeam">
                  <FinanceDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard/financial/applications"
              element={
                <PrivateRoute requiredRole="FinancialTeam">
                  <FinancialApplicationsList />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard/financial/review/:applicationId"
              element={
                <PrivateRoute requiredRole="FinancialTeam">
                  <FinancialApplicationReview />
                </PrivateRoute>
              }
            />
            <Route
              path="/lead/application-review/:applicationId"
              element={
                <PrivateRoute requiredRole="LeadGenerator">
                  <LeadApplicationDetails />
                </PrivateRoute>
              }
            />

            {/* Documents Routes - Accessible to all user types */}
            <Route
              path="/documents"
              element={
                <PrivateRoute>
                  <DocumentsPage />
                </PrivateRoute>
              }
            />

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
  );
}

// Main App Component
function App() {
  return (
    <AuthProvider>
      <AppRouter />
    </AuthProvider>
  );
}

export default App;
